import React, { useState, useEffect } from "react";
import axios from "axios";
import { server } from "../server";

const PrepareNewTestSeries = () => {
  const [testData, setTestData] = useState({
    title: "",
    description: "",
    subjects: [],
    exams: ["JEE Main"],
    testType: "Full",
    difficulty: "Mixed",
    totalQuestions: 0,
    totalMarks: 0,
    duration: 180,
    questionIds: [],
    randomizeQuestions: true,
    correctMark: 4,
    wrongMark: -1,
    hasNegativeMarking: true,
    status: "Draft",
    isActive: true,
    attemptsAllowed: 3,
    startDate: "",
    endDate: "",
    subjectId: "",
    chapterId: "",
    topicId: "",
  });

  const [subjects, setSubjects] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [topics, setTopics] = useState([]);
  const [allQuestions, setAllQuestions] = useState([]);
  const [availableQuestions, setAvailableQuestions] = useState([]);
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Filter states
  const [selectedSubject, setSelectedSubject] = useState("");
  const [selectedChapter, setSelectedChapter] = useState("");
  const [selectedTopic, setSelectedTopic] = useState("");

  // Fetch data
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);

      const [subjectsResponse, questionsResponse] = await Promise.all([
        axios.get(`${server}/subject/all`, {
          params: { pageNo: 1, pageSize: 100 },
        }),
        axios.get(`${server}/question/all`, {
          params: { pageNo: 1, pageSize: 200 },
        }),
      ]);

      if (subjectsResponse.data?.data) {
        setSubjects(subjectsResponse.data.data);
      }

      if (questionsResponse.data?.data) {
        setAllQuestions(questionsResponse.data.data);
        setAvailableQuestions(questionsResponse.data.data);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      alert("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  // Fetch chapters when subject is selected
  useEffect(() => {
    const fetchChapters = async () => {
      if (!selectedSubject) {
        setChapters([]);
        setSelectedChapter("");
        return;
      }

      try {
        const response = await axios.get(`${server}/chapter/all`, {
          params: {
            subjectId: selectedSubject,
            pageNo: 1,
            pageSize: 100,
          },
        });

        if (response.data?.data) {
          setChapters(response.data.data);
        } else {
          setChapters([]);
        }
        setSelectedChapter("");
        setSelectedTopic("");
      } catch (error) {
        console.error("Error fetching chapters:", error);
        setChapters([]);
      }
    };

    fetchChapters();
  }, [selectedSubject]);

  // Fetch topics when chapter is selected
  useEffect(() => {
    const fetchTopics = async () => {
      if (!selectedSubject || !selectedChapter) {
        setTopics([]);
        setSelectedTopic("");
        return;
      }

      try {
        const response = await axios.get(`${server}/topics/all`, {
          params: {
            subjectId: selectedSubject,
            chapterId: selectedChapter,
            pageNo: 1,
            pageSize: 100,
          },
        });

        if (response.data?.data) {
          setTopics(response.data.data);
        } else {
          setTopics([]);
        }
        setSelectedTopic("");
      } catch (error) {
        console.error("Error fetching topics:", error);
        setTopics([]);
      }
    };

    fetchTopics();
  }, [selectedSubject, selectedChapter]);

  // Filter available questions based on search and filters
  useEffect(() => {
    let filtered = allQuestions.filter(
      (q) => !selectedQuestions.find((sq) => sq.id === q.id)
    );

    // Apply search filter
    if (searchQuery) {
      filtered = filtered.filter(
        (q) =>
          q.question?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          q.topic?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          q.subject?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Apply test type specific filters
    if (testData.testType === "Subject" && selectedSubject) {
      filtered = filtered.filter((q) => q.subjectId === selectedSubject);
    } else if (testData.testType === "Chapter" && selectedChapter) {
      filtered = filtered.filter((q) => q.chapterId === selectedChapter);
    } else if (testData.testType === "Topic" && selectedTopic) {
      filtered = filtered.filter((q) => q.topicId === selectedTopic);
    }

    // Apply general subject filter for all types except when specific filters are applied
    if (
      selectedSubject &&
      !(testData.testType === "Chapter" && selectedChapter) &&
      !(testData.testType === "Topic" && selectedTopic)
    ) {
      filtered = filtered.filter((q) => q.subjectId === selectedSubject);
    }

    setAvailableQuestions(filtered);
  }, [
    searchQuery,
    selectedSubject,
    selectedChapter,
    selectedTopic,
    allQuestions,
    selectedQuestions,
    testData.testType,
  ]);

  // Update test data when selected questions change
  useEffect(() => {
    const questionIds = selectedQuestions.map((q) => q.id);
    const totalMarks = selectedQuestions.length * testData.correctMark;

    setTestData((prev) => ({
      ...prev,
      questionIds,
      totalQuestions: selectedQuestions.length,
      totalMarks,
      subjectId: selectedSubject || "",
      chapterId: selectedChapter || "",
      topicId: selectedTopic || "",
    }));
  }, [
    selectedQuestions,
    testData.correctMark,
    selectedSubject,
    selectedChapter,
    selectedTopic,
  ]);

  // Reset filters when test type changes
  useEffect(() => {
    setSelectedSubject("");
    setSelectedChapter("");
    setSelectedTopic("");
    setChapters([]);
    setTopics([]);
  }, [testData.testType]);

  // Add question to selected
  const addQuestion = (question) => {
    setSelectedQuestions((prev) => [...prev, question]);
  };

  // Remove question from selected
  const removeQuestion = (questionId) => {
    setSelectedQuestions((prev) => prev.filter((q) => q.id !== questionId));
  };

  // Move question up/down
  const moveQuestion = (index, direction) => {
    const newSelected = [...selectedQuestions];
    if (direction === "up" && index > 0) {
      [newSelected[index], newSelected[index - 1]] = [
        newSelected[index - 1],
        newSelected[index],
      ];
    } else if (direction === "down" && index < newSelected.length - 1) {
      [newSelected[index], newSelected[index + 1]] = [
        newSelected[index + 1],
        newSelected[index],
      ];
    }
    setSelectedQuestions(newSelected);
  };

  // Generate unique test code
  const generateTestCode = () => {
    const examCode =
      testData.exams[0]?.split(" ")?.join("")?.substring(0, 3)?.toUpperCase() ||
      "TST";
    const typeCode = testData.testType?.substring(0, 1)?.toUpperCase() || "F";
    const timestamp = Date.now().toString(36);
    return `${examCode}${typeCode}${timestamp}`.toUpperCase();
  };

  // Create test series
  const createTestSeries = async () => {
    try {
      if (!testData.title.trim()) {
        alert("Please enter test title");
        return;
      }

      if (selectedQuestions.length === 0) {
        alert("Please select at least one question");
        return;
      }

      setCreating(true);

      const payload = {
        title: testData.title,
        description: testData.description,
        code: generateTestCode(),
        subjects: testData.subjects,
        exams: testData.exams,
        testType: testData.testType,
        difficulty: testData.difficulty,
        totalQuestions: testData.totalQuestions,
        totalMarks: testData.totalMarks,
        duration: testData.duration * 60,
        questionIds: testData.questionIds,
        randomizeQuestions: testData.randomizeQuestions,
        correctMark: testData.correctMark,
        wrongMark: testData?.hasNegativeMarking ? testData.wrongMark : 0,
        hasNegativeMarking: testData.hasNegativeMarking,
        status: testData.status,
        isActive: testData.isActive,
        attemptsAllowed: testData.attemptsAllowed,
        startDate: testData.startDate || null,
        endDate: testData.endDate || null,
      };

      // Add hierarchy IDs based on test type
      if (testData.testType === "Subject" && selectedSubject) {
        payload.subjectId = selectedSubject;
      } else if (testData.testType === "Chapter" && selectedChapter) {
        payload.subjectId = selectedSubject;
        payload.chapterId = selectedChapter;
      } else if (testData.testType === "Topic" && selectedTopic) {
        payload.subjectId = selectedSubject;
        payload.chapterId = selectedChapter;
        payload.topicId = selectedTopic;
      }

      const response = await axios.post(
        `${server}/mockTestSeries/new`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.data.success) {
        alert("Test series created successfully!");
        // Reset form
        setTestData({
          title: "",
          description: "",
          subjects: [],
          exams: ["JEE Main"],
          testType: "Full",
          difficulty: "Mixed",
          totalQuestions: 0,
          totalMarks: 0,
          duration: 180,
          questionIds: [],
          randomizeQuestions: true,
          correctMark: 4,
          wrongMark: -1,
          hasNegativeMarking: true,
          status: "Draft",
          isActive: true,
          attemptsAllowed: 3,
          startDate: "",
          endDate: "",
          subjectId: "",
          chapterId: "",
          topicId: "",
        });
        setSelectedQuestions([]);
        setSearchQuery("");
        setSelectedSubject("");
        setSelectedChapter("");
        setSelectedTopic("");
        setChapters([]);
        setTopics([]);
      }
    } catch (error) {
      console.error("Error creating test series:", error);
      alert(error.response?.data?.message || "Failed to create test series");
    } finally {
      setCreating(false);
    }
  };

  const testTypes = [
    { value: "Full", label: "Full Length", icon: "📊" },
    { value: "Subject", label: "Subject Wise", icon: "📚" },
    { value: "Chapter", label: "Chapter Wise", icon: "📖" },
    { value: "Topic", label: "Topic Wise", icon: "🎯" },
  ];

  const difficulties = [
    { value: "Easy", label: "Easy", color: "bg-green-500" },
    { value: "Medium", label: "Medium", color: "bg-yellow-500" },
    { value: "Hard", label: "Hard", color: "bg-red-500" },
    { value: "Mixed", label: "Mixed", color: "bg-purple-500" },
  ];

  const exams = [
    "JEE Main",
    "JEE Advanced",
    "NEET",
    "UPSC",
    "SSC",
    "Bank PO",
    "GATE",
    "CAT",
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-lg">Loading questions...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <div className="border-b border-gray-700 px-6 py-4 bg-gray-800">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold">Create Test Series</h1>
            <p className="text-gray-400 text-sm mt-1">
              Design your custom test with selected questions
            </p>
          </div>
          <button
            onClick={createTestSeries}
            disabled={creating}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 px-6 py-2 rounded-lg font-medium transition-colors flex items-center gap-2"
          >
            {creating ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Creating...
              </>
            ) : (
              "Create Test Series"
            )}
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        {/* Test Configuration */}
        <div className="bg-gray-800 rounded-xl p-6 mb-6 border border-gray-700">
          <h2 className="text-lg font-semibold mb-4">Test Configuration</h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Basic Information */}
            <div className="space-y-4 p-6 rounded-2xl bg-gray-950/60 border border-gray-950">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Test Title *
                </label>
                <input
                  type="text"
                  value={testData.title}
                  onChange={(e) =>
                    setTestData((prev) => ({ ...prev, title: e.target.value }))
                  }
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-blue-500 transition-colors"
                  placeholder="e.g., JEE Main Full Test 1"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Description
                </label>
                <textarea
                  value={testData.description}
                  onChange={(e) =>
                    setTestData((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  rows={3}
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-blue-500 transition-colors resize-none"
                  placeholder="Describe the test series..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Duration (minutes)
                  </label>
                  <input
                    type="number"
                    value={testData.duration}
                    onChange={(e) =>
                      setTestData((prev) => ({
                        ...prev,
                        duration: parseInt(e.target.value) || 180,
                      }))
                    }
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-blue-500 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Attempts Allowed
                  </label>
                  <input
                    type="number"
                    value={testData.attemptsAllowed}
                    onChange={(e) =>
                      setTestData((prev) => ({
                        ...prev,
                        attemptsAllowed: parseInt(e.target.value) || 3,
                      }))
                    }
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-blue-500 transition-colors"
                    min="1"
                  />
                </div>
              </div>
            </div>

            {/* Test Settings */}
            <div className="space-y-4 p-6 rounded-2xl bg-gray-950/60 border border-gray-950">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-3">
                  Difficulty Level
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {difficulties.map((diff) => (
                    <button
                      key={diff.value}
                      onClick={() =>
                        setTestData((prev) => ({
                          ...prev,
                          difficulty: diff.value,
                        }))
                      }
                      className={`p-3 rounded-lg border transition-all ${
                        testData.difficulty === diff.value
                          ? "border-blue-500 bg-blue-500/10"
                          : "border-gray-700 bg-gray-900 hover:border-gray-600"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <div
                          className={`w-3 h-3 rounded-full ${diff.color}`}
                        ></div>
                        <span className="text-sm font-medium">
                          {diff.label}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-3">
                  Target Exams
                </label>
                <div className="max-h-40 overflow-y-auto bg-gray-900 border border-gray-700 rounded-lg p-3">
                  <div className="grid grid-cols-2 gap-2">
                    {exams.map((exam) => (
                      <label
                        key={exam}
                        className="flex items-center space-x-2 p-2 rounded hover:bg-gray-800 transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={testData.exams.includes(exam)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setTestData((prev) => ({
                                ...prev,
                                exams: [...prev.exams, exam],
                              }));
                            } else {
                              setTestData((prev) => ({
                                ...prev,
                                exams: prev.exams.filter((e) => e !== exam),
                              }));
                            }
                          }}
                          className="w-4 rounded bg-gray-800 border-gray-600 text-blue-500 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-300">{exam}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Scoring Pattern */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-300 mb-3">
              Scoring Pattern
            </label>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs text-gray-400 mb-2">
                  Correct Answer Marks
                </label>
                <input
                  type="number"
                  value={testData.correctMark}
                  onChange={(e) =>
                    setTestData((prev) => ({
                      ...prev,
                      correctMark: parseFloat(e.target.value) || 4,
                    }))
                  }
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-green-500 transition-colors"
                  step="0.5"
                />
              </div>
              {testData?.hasNegativeMarking && (
                <div>
                  <label className="block text-xs text-gray-400 mb-2">
                    Wrong Answer Marks
                  </label>
                  <input
                    type="number"
                    value={testData.wrongMark}
                    onChange={(e) =>
                      setTestData((prev) => ({
                        ...prev,
                        wrongMark: parseFloat(e.target.value) || -1,
                      }))
                    }
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-red-500 transition-colors"
                    step="0.5"
                  />
                </div>
              )}
              <div className="flex items-center justify-between p-3 bg-gray-900 rounded-lg border border-gray-700">
                <div>
                  <span className="text-sm font-medium text-gray-300">
                    Negative Marking
                  </span>
                  <p className="text-xs text-gray-500 mt-1">
                    Enable negative marks for wrong answers
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={testData.hasNegativeMarking}
                    onChange={(e) =>
                      setTestData((prev) => ({
                        ...prev,
                        hasNegativeMarking: e.target.checked,
                      }))
                    }
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-5 peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-4 gap-4">
            <div className="bg-gray-900 rounded-lg p-4 text-center border border-gray-700">
              <div className="text-2xl font-bold text-blue-400">
                {selectedQuestions.length}
              </div>
              <div className="text-sm text-gray-400">Questions</div>
            </div>
            <div className="bg-gray-900 rounded-lg p-4 text-center border border-gray-700">
              <div className="text-2xl font-bold text-green-400">
                {testData.totalMarks}
              </div>
              <div className="text-sm text-gray-400">Total Marks</div>
            </div>
            <div className="bg-gray-900 rounded-lg p-4 text-center border border-gray-700">
              <div className="text-2xl font-bold text-yellow-400">
                {testData.duration}
              </div>
              <div className="text-sm text-gray-400">Minutes</div>
            </div>
            <div className="bg-gray-900 rounded-lg p-4 text-center border border-gray-700">
              <div className="text-2xl font-bold text-purple-400">
                {testData.attemptsAllowed}
              </div>
              <div className="text-sm text-gray-400">Attempts</div>
            </div>
          </div>
        </div>

        {/* Dual Panel Question Selection */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Available Questions Panel */}
          <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold">Available Questions</h3>
                <p className="text-sm text-gray-400">
                  Select questions to add to your test
                </p>
              </div>
              <span className="bg-blue-500/20 text-blue-400 px-3 py-1 rounded-full text-sm font-medium">
                {availableQuestions.length} questions
              </span>
            </div>

            {/* Test Type Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Test Type
              </label>
              <div className="grid grid-cols-4 gap-2">
                {testTypes.map((type) => (
                  <button
                    key={type.value}
                    onClick={() =>
                      setTestData((prev) => ({
                        ...prev,
                        testType: type.value,
                      }))
                    }
                    className={`p-3 rounded-lg border transition-all text-sm ${
                      testData.testType === type.value
                        ? "border-blue-500 bg-blue-500/10 text-white"
                        : "border-gray-700 bg-gray-900 text-gray-300 hover:border-gray-600"
                    }`}
                  >
                    <div className="text-base mb-1">{type.icon}</div>
                    {type.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Filters Row */}
            <div className="mb-4 space-y-3">
              <div className="flex items-center bg-gray-900 rounded-lg px-4 py-3 border border-gray-700 focus-within:border-blue-500 transition-colors">
                <svg
                  className="w-5 h-5 text-gray-400 mr-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="flex-1 bg-transparent text-white focus:outline-none placeholder-gray-500"
                  placeholder="Search by question, topic, or subject..."
                />
              </div>

              {/* Subject, Chapter, Topic Filters in One Row */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {/* Subject Filter */}
                <div>
                  <label className="block text-xs font-medium text-gray-400 mb-1">
                    Subject
                  </label>
                  <select
                    value={selectedSubject}
                    onChange={(e) => setSelectedSubject(e.target.value)}
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-blue-500 transition-colors text-sm"
                  >
                    <option value="">All Subjects</option>
                    {subjects.map((subject) => (
                      <option key={subject.id} value={subject.id}>
                        {subject.subject}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Chapter Filter - Visible for Chapter and Topic test types */}
                {(testData.testType === "Chapter" ||
                  testData.testType === "Topic") && (
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1">
                      Chapter
                    </label>
                    <select
                      value={selectedChapter}
                      onChange={(e) => setSelectedChapter(e.target.value)}
                      disabled={!selectedSubject}
                      className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-blue-500 transition-colors text-sm disabled:opacity-50"
                    >
                      <option value="">All Chapters</option>
                      {chapters.map((chapter) => (
                        <option key={chapter.id} value={chapter.id}>
                          {chapter.chapter}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Topic Filter - Visible for Topic test type */}
                {testData.testType === "Topic" && (
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1">
                      Topic
                    </label>
                    <select
                      value={selectedTopic}
                      onChange={(e) => setSelectedTopic(e.target.value)}
                      disabled={!selectedChapter}
                      className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-blue-500 transition-colors text-sm disabled:opacity-50"
                    >
                      <option value="">All Topics</option>
                      {topics.map((topic) => (
                        <option key={topic.id} value={topic.id}>
                          {topic.topic}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>

            {/* Available Questions List */}
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {availableQuestions.map((question) => (
                <div
                  key={question.id}
                  className="p-4 bg-gray-900 rounded-lg border border-gray-700 hover:border-blue-500 transition-colors cursor-pointer group"
                  onClick={() => addQuestion(question)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="px-2 py-1 bg-gray-800 rounded text-xs font-medium">
                          {question.subject}
                        </span>
                        <span
                          className={`px-2 py-1 rounded text-xs font-medium ${
                            question.difficulty === "Easy"
                              ? "bg-green-500/20 text-green-400"
                              : question.difficulty === "Medium"
                              ? "bg-yellow-500/20 text-yellow-400"
                              : "bg-red-500/20 text-red-400"
                          }`}
                        >
                          {question.difficulty}
                        </span>
                      </div>
                      <p className="text-gray-200 text-sm line-clamp-2 mb-1">
                        {question.question}
                      </p>
                      {question.topic && (
                        <p className="text-xs text-gray-400">
                          Topic: {question.topic}
                        </p>
                      )}
                    </div>
                    <button className="ml-2 p-2 bg-blue-600 rounded text-white opacity-0 group-hover:opacity-100 transition-opacity">
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 4v16m8-8H4"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}

              {availableQuestions.length === 0 && (
                <div className="text-center py-8 text-gray-400">
                  <div className="text-2xl mb-2">🔍</div>
                  <div>No questions found matching your criteria</div>
                </div>
              )}
            </div>
          </div>

          {/* Selected Questions Panel */}
          <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold">Selected Questions</h3>
                <p className="text-sm text-gray-400">
                  Manage your test questions
                </p>
              </div>
              <span className="bg-green-500/20 text-green-400 px-3 py-1 rounded-full text-sm font-medium">
                {selectedQuestions.length} selected
              </span>
            </div>

            {/* Selected Questions List */}
            <div className="space-y-3 max-h-[507px] overflow-y-auto">
              {selectedQuestions.map((question, index) => (
                <div
                  key={question.id}
                  className="p-4 bg-blue-500/10 rounded-lg border border-blue-500/30"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="px-2 py-1 bg-blue-600 rounded text-xs font-medium text-white">
                          #{index + 1}
                        </span>
                        <span className="px-2 py-1 bg-blue-500/20 rounded text-xs font-medium text-blue-400">
                          {question.subject}
                        </span>
                        <span
                          className={`px-2 py-1 rounded text-xs font-medium ${
                            question.difficulty === "Easy"
                              ? "bg-green-500/20 text-green-400"
                              : question.difficulty === "Medium"
                              ? "bg-yellow-500/20 text-yellow-400"
                              : "bg-red-500/20 text-red-400"
                          }`}
                        >
                          {question.difficulty}
                        </span>
                      </div>
                      <p className="text-gray-200 text-sm line-clamp-2 mb-1">
                        {question.question}
                      </p>
                      {question.topic && (
                        <p className="text-xs text-gray-400">
                          Topic: {question.topic}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col space-y-1 ml-2">
                      <button
                        onClick={() => moveQuestion(index, "up")}
                        disabled={index === 0}
                        className="p-1 bg-gray-700 rounded text-gray-300 hover:text-white hover:bg-gray-600 disabled:opacity-30 transition-colors"
                        title="Move up"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 15l7-7 7 7"
                          />
                        </svg>
                      </button>
                      <button
                        onClick={() => removeQuestion(question.id)}
                        className="p-1 bg-red-500/20 rounded text-red-400 hover:text-red-300 hover:bg-red-500/30 transition-colors"
                        title="Remove"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                      <button
                        onClick={() => moveQuestion(index, "down")}
                        disabled={index === selectedQuestions.length - 1}
                        className="p-1 bg-gray-700 rounded text-gray-300 hover:text-white hover:bg-gray-600 disabled:opacity-30 transition-colors"
                        title="Move down"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              ))}

              {selectedQuestions.length === 0 && (
                <div className="text-center py-8 text-gray-400">
                  <div className="text-2xl mb-2">📝</div>
                  <div>No questions selected yet</div>
                  <div className="text-sm mt-1">
                    Select questions from the left panel to build your test
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrepareNewTestSeries;
